import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { IPaginatedResponse } from "../models/Pagination";
import {
  ICreateProjectDocuments,
  ICreateProjectMember,
  ICreateProjectPhase,
  ICreateProjectPhaseMilestone,
  ICreateResearchProject,
  ICreateResearchPurchaseRequest,
  IFilterResearch,
  IListMaterialAwb,
  IProjectBudgetCategoryResponse,
  IProjectDocumentDownloadHistory,
  IProjectDocuments,
  IProjectMember,
  IProjectOverview,
  IProjectPhaseMilestone,
  IProjectPhases,
  IProjectPurchaseRequest,
  IResearchProject,
  IResearchPurchaseRequest,
  IUpdateProjectPhase,
  IUpdateResearchProject,
  IUpdateResearchPurchaseRequest,
} from "../models/Research";
import { researchService } from "../services/researchService";
export function useGetAllResearchProject(
  params: IFilterResearch,
): UseQueryResult<IPaginatedResponse<IResearchProject>> {
  return useQuery({
    queryKey: ["research-project-all", params],
    queryFn: () => researchService.getAllResearchProject(params),
  });
}
export function useGetResearchProject(
  alias: string | undefined,
): UseQueryResult<IResearchProject> {
  return useQuery({
    queryKey: ["research-project", alias],
    queryFn: () => researchService.getResearchProjectByAlias(alias!),
    enabled: !!alias,
  });
}
export function useGetResearchProjectBudget(
  id: number,
): UseQueryResult<IProjectBudgetCategoryResponse> {
  return useQuery({
    queryKey: ["research-project-budget", id],
    queryFn: () => researchService.getResearchProjectBudget(id),
    enabled: !!id,
  });
}

export function useCreateResearchProject(): UseMutationResult<
  IResearchProject,
  unknown,
  ICreateResearchProject,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateResearchProject) =>
      researchService.createResearchProject(data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        {
          queryKey: ["research-project-all"],
        },
        (oldData: IPaginatedResponse<IResearchProject>) => {
          if (oldData) {
            return {
              ...oldData,
              data: [data, ...oldData.data],
            };
          }
          return oldData;
        },
      );
    },
  });
}
export function useGetResearchProjectPurchaseRequest(
  id: number,
  partNumber?: string,
): UseQueryResult<IProjectPurchaseRequest> {
  return useQuery({
    queryKey: ["research-project-purchase-request", id, partNumber],
    queryFn: () =>
      researchService.getResearchProjectPurchaseRequest(id, partNumber),
    enabled: !!id,
  });
}
export function useUpdateResearchProjectPurchaseRequest(): UseMutationResult<
  IResearchPurchaseRequest,
  unknown,
  { id: number; data: IUpdateResearchPurchaseRequest },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: number; data: IUpdateResearchPurchaseRequest }) =>
      researchService.updatePurchaseRequest(body.id, body.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["research-project-purchase-request"],
        predicate: (query) =>
          query.queryKey[0] === "research-project-purchase-request",
      });
    },
  });
}
export function useApprovePurchaseRequestByDirector(): UseMutationResult<
  IResearchPurchaseRequest,
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      researchService.approvePurchaseRequestByDirector(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["research-project-purchase-request"],
        predicate: (query) =>
          query.queryKey[0] === "research-project-purchase-request",
      });
    },
  });
}
export function useRequestPurchaseRequestForDirector(): UseMutationResult<
  IResearchPurchaseRequest,
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      researchService.requestPurchaseRequestForDirector(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["research-project-purchase-request"],
        predicate: (query) =>
          query.queryKey[0] === "research-project-purchase-request",
      });
    },
  });
}

export function useCreatePurchaseRequest(): UseMutationResult<
  IResearchPurchaseRequest,
  unknown,
  ICreateResearchPurchaseRequest,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateResearchPurchaseRequest) =>
      researchService.createPurchaseRequest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["research-project-purchase-request"],
        predicate: (query) =>
          query.queryKey[0] === "research-project-purchase-request",
      });
    },
  });
}
export function useUpdateResearchProject(): UseMutationResult<
  IResearchProject,
  unknown,
  { id: number; data: IUpdateResearchProject },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: number; data: IUpdateResearchProject }) =>
      researchService.updateResearchProject(body.id, body.data),
    onSuccess: (updatedProject) => {
      queryClient.setQueriesData(
        { queryKey: ["research-project-all"] },
        (oldData: IPaginatedResponse<IResearchProject> | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((item) =>
              item.id === updatedProject.id ? updatedProject : item,
            ),
          };
        },
      );
      queryClient.setQueriesData(
        { queryKey: ["research-project"], alias: updatedProject.alias },
        (oldData: IResearchProject | undefined) => {
          if (!oldData) return oldData;
          return {
            ...updatedProject,
          };
        },
      );
    },
  });
}
export function useGetProjectOverview(): UseQueryResult<IProjectOverview> {
  return useQuery({
    queryKey: ["project-overview"],
    queryFn: () => researchService.getProjectOverview(),
  });
}

export function useGetListAwbByMaterialId(
  materialId: number,
): UseQueryResult<IListMaterialAwb[]> {
  return useQuery({
    queryKey: ["material-awb", materialId],
    queryFn: () => researchService.getListAwbByMaterialId(materialId),
  });
}
export function useUploadDocumentResearch(): UseMutationResult<
  IProjectDocuments,
  unknown,
  ICreateProjectDocuments,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateProjectDocuments) =>
      researchService.uploadDocumentResearch(data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        { queryKey: ["document-project", data.projectId] },
        (oldData: IProjectDocuments[] | undefined) => {
          if (!oldData) return [data];
          return [...oldData, data];
        },
      );
    },
  });
}
export function useGetDocumentByProjectId(
  projectId: number,
): UseQueryResult<IProjectDocuments[]> {
  return useQuery({
    queryKey: ["document-project", projectId],
    queryFn: () => researchService.getDocumentByProjectId(projectId),
  });
}
export function useGetHistoryDownloadDocument(
  documentId: number,
): UseQueryResult<IProjectDocumentDownloadHistory[]> {
  return useQuery({
    queryKey: ["history-download-document", documentId],
    queryFn: () => researchService.getDownloadHistoryDocument(documentId),
  });
}
export function useDeleteDocumentProject(): UseMutationResult<
  number,
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => researchService.deleteDocumentProject(id),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData(
        { queryKey: ["document-project", data] },
        (oldData: IProjectDocuments[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((item) => item.id !== variables);
        },
      );
    },
  });
}
export function useGetProjectMember(
  projectId: number,
): UseQueryResult<IProjectMember[]> {
  return useQuery({
    queryKey: ["project-member", projectId],
    queryFn: () => researchService.getProjectMembers(projectId),
  });
}

export function useCreateBulkProjectMember(): UseMutationResult<
  IProjectMember[],
  unknown,
  ICreateProjectMember,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: ICreateProjectMember) =>
      researchService.addBulkProjectMember(projectId, data),
    onSuccess: (returnedData, variables) => {
      const { projectId } = variables;
      queryClient.setQueriesData(
        { queryKey: ["project-member", projectId] },
        (oldData: IProjectMember[] | undefined) => {
          if (!oldData) return returnedData;
          return [...oldData, ...returnedData];
        },
      );
    },
  });
}
export function useDeleteProjectMember(): UseMutationResult<
  number,
  unknown,
  { id: number; projectId: number },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: number; projectId: number }) =>
      researchService.deleteProjectMember(data.id),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData(
        { queryKey: ["project-member", variables.projectId] },
        (oldData: IProjectMember[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((item) => item.id !== variables.id);
        },
      );
    },
  });
}

export function useGetProjectPhases(
  projectId: number | undefined,
): UseQueryResult<IProjectPhases[]> {
  return useQuery({
    queryKey: ["project-phase", projectId],
    queryFn: () => researchService.getProjectPhases(projectId!),
    enabled: !!projectId,
  });
}
export function useUpdateProjectPhases(): UseMutationResult<
  IProjectPhases,
  unknown,
  { id: number; data: IUpdateProjectPhase },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: number; data: IUpdateProjectPhase }) =>
      researchService.updateProjectPhases(body.id, body.data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        { queryKey: ["project-phase", data.projectId] },
        (oldData: IProjectPhases[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((item) => (item.id === data.id ? data : item));
        },
      );
    },
  });
}

export function useCreateProjectPhases(): UseMutationResult<
  IProjectPhases,
  unknown,
  ICreateProjectPhase,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateProjectPhase) =>
      researchService.createProjectPhases(data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        { queryKey: ["project-phase", data.projectId] },
        (oldData: IProjectPhases[] | undefined) => {
          if (!oldData) return [data];
          return [...oldData, data];
        },
      );
    },
  });
}
export function useDeleteProjectPhases(): UseMutationResult<
  number,
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => researchService.deleteProjectPhases(data),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData(
        { queryKey: ["project-phase", data] },
        (oldData: IProjectPhases[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((item) => item.id !== variables);
        },
      );
    },
  });
}
export function useDeleteProjectPhaseMilestone(): UseMutationResult<
  number,
  unknown,
  { id: number; projectId: number },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: number; projectId: number }) =>
      researchService.deleteProjectPhaseMilestone(data.id),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData(
        { queryKey: ["project-phase", variables.projectId] },
        (oldData: IProjectPhases[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((item) => {
            if (item.id === data.id) {
              return {
                ...item,
                projectPhaseMilestones: item.projectPhaseMilestones?.filter(
                  (milestone) => milestone.id !== variables.id,
                ),
              };
            }
            return item;
          });
        },
      );
    },
  });
}
export function useCreateProjectPhaseMilestone(
  projectId: number,
): UseMutationResult<
  IProjectPhaseMilestone,
  unknown,
  ICreateProjectPhaseMilestone
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateProjectPhaseMilestone) =>
      researchService.createProjectPhaseMilestone(data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        { queryKey: ["project-phase", projectId] },
        (oldData: IProjectPhases[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((item) => {
            if (item.id === data.projectPhaseId) {
              return {
                ...item,
                projectPhaseMilestones: [
                  ...(item.projectPhaseMilestones || []),
                  data,
                ],
              };
            }
            return item;
          });
        },
      );
    },
  });
}
